import {
  ChevronDown,
  Settings,
  LogOut,
  Building2,
  User,
} from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { cn } from '../lib/utils';
import { useAppDispatch } from '../lib/reduxHooks';
import { logoutUser } from '../store/slices/auth';

interface UserProfileDropdownProps {
  onLogout?: () => void;
  userEmail?: string;
  userName?: string;
  avatarUrl?: string;
  organization?: string;
  /** Text + hover classes for the trigger button (e.g. the top bar's header colors); name + chevron inherit the color. */
  triggerClassName?: string;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  onLogout,
  userEmail,
  userName = "Guest",
  avatarUrl,
  organization,
  triggerClassName
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const menuItems = [
    {
      label: 'Profile',
      icon: User,
      href: '/profile',
      description: 'Your account details'
    },
    {
      label: 'Organization',
      icon: Building2,
      href: '/settings/organization',
      description: organization || 'View organization details'
    },
    {
      label: 'Settings',
      icon: Settings,
      href: '/settings',
      description: 'System preferences'
    }
  ];

  const handleLogout = async () => {
    setIsOpen(false);
    try {
      // Dispatch Redux logout action
      await dispatch(logoutUser()).unwrap();

      // Navigate to login page after successful logout
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout fails
      navigate('/login');
    }
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
          "border border-transparent",
          triggerClassName ?? "hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700"
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User profile menu"
      >
        {/* Avatar or Default Icon */}
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={userName}
            className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
            {getInitials(userName)}
          </div>
        )}
        
        {/* Name and Chevron (hidden on mobile) */}
        <div className="hidden sm:flex items-center gap-1">
          <div className="text-left">
            <div className={cn("text-sm font-medium truncate max-w-32", !triggerClassName && "text-gray-900 dark:text-gray-100")}>
              {userName}
            </div>
          </div>
          <ChevronDown 
            className={cn(
              "w-4 h-4 transition-transform duration-200",
              !triggerClassName && "text-gray-500 dark:text-gray-400",
              isOpen ? 'rotate-180' : 'rotate-0'
            )} 
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={cn(
            "absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900",
            "rounded-lg shadow-xl border border-gray-200 dark:border-gray-700",
            "py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-200"
          )}
          role="menu"
          aria-orientation="vertical"
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={userName}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-semibold">
                  {getInitials(userName)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {userName}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {userEmail}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                  {organization}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item) => {
              const Icon = item.icon;

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5",
                    "text-sm text-gray-700 dark:text-gray-300",
                    "hover:bg-gray-50 dark:hover:bg-gray-800",
                    "transition-colors duration-150"
                  )}
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                  <Icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <div className="flex-1">
                    <div className="font-medium">{item.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {item.description}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Logout Section */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-2">
            <button
              onClick={handleLogout}
              className={cn(
                "flex items-center gap-3 px-4 py-2.5",
                "text-sm text-red-600 dark:text-red-400",
                "hover:bg-red-50 dark:hover:bg-red-900/20",
                "transition-colors duration-150 w-full text-left"
              )}
              role="menuitem"
            >
              <LogOut className="w-5 h-5" />
              <div className="flex-1">
                <div className="font-medium">Logout</div>
                <div className="text-xs text-red-500/70 dark:text-red-400/70">
                  End your session
                </div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;